/**
 * Messages Tab Component for Event Details Dialog
 * Real-time messaging between DJ and client
 */
import { useState, useRef, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { MessageSquare } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MessagesTab({ 
  booking, 
  messages = [], 
  onMessagesUpdate
}) {
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      await axios.post(`${API}/bookings/${booking.id}/messages`, {
        booking_id: booking.id,
        content: newMessage.trim(),
        sender_type: 'dj'
      }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      setNewMessage('');
      onMessagesUpdate();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Client Communications</h3>
        <span className="text-sm text-muted-foreground">
          {messages.length} message{messages.length !== 1 ? 's' : ''}
        </span>
      </div>
      
      {/* Messages List */}
      <div className="h-[300px] overflow-y-auto space-y-3 p-4 bg-muted/20 rounded-xl">
        {messages.length > 0 ? (
          <>
            {messages.map((msg) => (
              <div 
                key={msg.id}
                className={`flex ${msg.sender_type === 'dj' ? 'justify-end' : 'justify-start'}`}
              >
                <div 
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.sender_type === 'dj' 
                      ? 'bg-primary text-primary-foreground rounded-br-sm' 
                      : 'bg-muted rounded-bl-sm'
                  }`}
                >
                  <p className="text-sm">{msg.content}</p>
                  <div className={`text-xs mt-1 ${msg.sender_type === 'dj' ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                    {msg.sender_name || (msg.sender_type === 'dj' ? 'You' : booking?.client_name)} • {new Date(msg.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </>
        ) : (
          <div className="text-center py-8 text-muted-foreground">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No messages yet</p>
            <p className="text-sm">Start the conversation with your client</p>
          </div>
        )}
      </div>
      
      {/* Send Message Input */}
      <div className="flex gap-2">
        <textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder="Type a message to your client..."
          className="flex-1 min-h-[60px] bg-background border border-border rounded-lg p-3 text-foreground resize-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          data-testid="message-input"
        />
        <Button 
          onClick={handleSend} 
          disabled={sending || !newMessage.trim()}
          className="bg-primary text-primary-foreground hover:bg-primary/80 self-end"
          data-testid="send-message-btn"
        >
          {sending ? 'Sending...' : 'Send'}
        </Button>
      </div>
    </div>
  );
}
